import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";

const POLL_ATTEMPTS = 10;
const POLL_INTERVAL_MS = 2000;

export default function ProtectionSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | active | pending | error
  const [contract, setContract] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let cancelled = false;

    const poll = async () => {
      try {
        const { data } = await api.get(`/protection/payment-status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          const { data: c } = await api.get(`/protection/contract-by-session/${sessionId}`);
          if (cancelled) return;
          setContract(c);
          setState("active");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= POLL_ATTEMPTS) { setState("pending"); return; }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        if (!cancelled) setState("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <section data-testid="protection-success" className="relative z-10 bg-[#050505] py-20 lg:py-28 min-h-[70vh]">
      <div className="max-w-2xl mx-auto px-6 lg:px-10">
        {state === "checking" && (
          <div data-testid="protection-success-checking" className="glass rounded-3xl p-10 lg:p-14 text-center">
            <Loader2 size={40} className="animate-spin text-white mx-auto mb-6" />
            <h2 className="font-display text-3xl">Zahlung wird bestätigt…</h2>
            <p className="text-[#aaa] mt-3">Einen Moment bitte, wir prüfen deine Zahlung bei Stripe.</p>
          </div>
        )}

        {state === "active" && contract && (
          <div data-testid="protection-success-confirmed" className="glass rounded-3xl p-10 lg:p-14">
            <CheckCircle2 size={48} className="text-emerald-400 mb-6" />
            <h2 className="font-display text-4xl lg:text-5xl">Schutzpaket aktiv.</h2>
            <p className="text-[#aaa] mt-4 leading-relaxed">
              Danke, {contract.first_name}! Dein Schutzpaket für dein {contract.device} ist ab jetzt aktiv.
              Deine Vertragsnummer lautet <span className="text-white font-mono">{contract.contract_number}</span> –
              bewahre sie gut auf, du brauchst sie für Reparaturen und zur Vertragsverwaltung.
            </p>
            <div className="mt-6 rounded-2xl border border-white/10 bg-[#0a0a0a] p-6 text-sm space-y-2">
              <div className="flex justify-between"><span className="text-[#888]">Monatsbeitrag</span><span className="font-mono text-white">{contract.monthly_price.toFixed(2)} €</span></div>
              <div className="flex justify-between"><span className="text-[#888]">Mindestlaufzeit bis</span><span className="text-white">{new Date(contract.min_term_end_date).toLocaleDateString("de-DE")}</span></div>
              <div className="flex justify-between"><span className="text-[#888]">Reparaturen/Jahr</span><span className="text-white">{contract.repairs_per_year}</span></div>
            </div>
            <p className="text-[#888] text-sm mt-6">Eine Bestätigungsmail mit allen Details ist unterwegs zu dir.</p>
            <Link to="/" data-testid="protection-success-home" className="mt-8 inline-block text-sm text-white underline underline-offset-4 hover:text-[#ccc] transition-colors">
              Zurück zur Startseite
            </Link>
          </div>
        )}

        {state === "pending" && (
          <div data-testid="protection-success-pending" className="glass rounded-3xl p-10 lg:p-14 text-center">
            <Loader2 size={40} className="text-amber-400 mx-auto mb-6" />
            <h2 className="font-display text-3xl">Zahlung wird noch verarbeitet.</h2>
            <p className="text-[#aaa] mt-3">Das kann bei manchen Zahlungsarten etwas dauern. Du erhältst in Kürze eine Bestätigungsmail.</p>
          </div>
        )}

        {state === "error" && (
          <div data-testid="protection-success-error" className="glass rounded-3xl p-10 lg:p-14 text-center">
            <XCircle size={40} className="text-red-400 mx-auto mb-6" />
            <h2 className="font-display text-3xl">Etwas ist schiefgelaufen.</h2>
            <p className="text-[#aaa] mt-3">Wir konnten deine Zahlung nicht zuordnen. Bitte kontaktiere uns, falls eine Abbuchung erfolgt ist.</p>
          </div>
        )}
      </div>
    </section>
  );
}
